import './App.scss';
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import { useEffect, useState } from 'react';
import { Spin } from 'antd';
import { useIsAuthenticated, useMsal } from '@azure/msal-react';
import { InteractionStatus } from '@azure/msal-browser';
import SplitText from './components/ReactBits/SplitText';
import { BRAND_NAME } from './constants/branding';
import { AuthProvider } from './context/AuthContext';
import { UserProfileProvider } from './context/UserProfileContext';
import { CommonProvider } from './context/CommonContext';
import { MessageProvider } from './context/MessageProvider';
import { SidebarProvider } from './context/SidebarContext';
import { ClientProvider } from './context/ClientContext';
import { AgentProvider } from './context/AgentContext';
import { useSessionGuard } from './hooks/useSessionGuard';
import { useIdleTimeout } from './hooks/useIdleTimeout';
import LoginPage from './Pages/Auth/LoginPage';
import WorkspacePage from './Pages/Workspace/WorkspacePage';
import JsonConfigTool from './Pages/Config/JsonConfigTool';
import TenantOnboarding from './Pages/Onboarding/TenantOnboarding';
import ProtectedRoute from './components/ProtectedRoute/ProtectedRoute';
import NotFound from './Pages/NotFound/NotFound';
import HealthPage from './Pages/Health/HealthPage';

const RootRedirect = () => {
  const isAuthenticated = useIsAuthenticated();
  const { inProgress } = useMsal();

  if (inProgress !== InteractionStatus.None) {
    return (
      <div className="flex justify-center items-center h-screen">
        <Spin size="large" />
      </div>
    );
  }

  return <Navigate to={isAuthenticated ? '/workspace' : '/login'} replace />;
};

/**
 * Runs inside BrowserRouter so that useSessionGuard has access to
 * React Router context. Validates the active session on every route
 * change and on a periodic interval.
 */
const SessionGuardWrapper: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  useSessionGuard({ excludedPaths: ['/login', '/health'] });
  useIdleTimeout({ excludedPaths: ['/login', '/health'] });
  return <>{children}</>;
};

function App() {
  const [loading, setLoading] = useState(true);
  useEffect(() => {
    const t = setTimeout(() => setLoading(false), 1000);
    return () => clearTimeout(t);
  }, []);

  if (loading) {
    return (
      <div className="h-screen flex justify-center items-center bg-gray-900 text-white">
        <SplitText
          text={`Welcome to ${BRAND_NAME}!`}
          className="text-2xl font-semibold text-center"
          delay={30}
          animationTo={{ opacity: 1, transform: 'translate3d(0,0,0)' }}
          threshold={0.2}
          rootMargin="-50px"
        />
      </div>
    );
  }

  return (
    <AuthProvider>
      <UserProfileProvider>
      <ClientProvider>
        <AgentProvider>
          <CommonProvider>
            <MessageProvider>
              <SidebarProvider>
                <BrowserRouter>
                  <SessionGuardWrapper>
                  <Routes>
                    <Route path="/login" element={<LoginPage />} />
                    <Route path="/" element={<RootRedirect />} />
                    <Route path="/health" element={<HealthPage />} />
                    <Route
                      path="/workspace"
                      element={
                        <ProtectedRoute>
                          <WorkspacePage />
                        </ProtectedRoute>
                      }
                    />
                    <Route
                      path="/config"
                      element={
                        <ProtectedRoute>
                          <JsonConfigTool />
                        </ProtectedRoute>
                      }
                    />
                    <Route
                      path="/onboarding"
                      element={
                        <ProtectedRoute>
                          <TenantOnboarding />
                        </ProtectedRoute>
                      }
                    />
                    <Route path="*" element={<NotFound />} />
                  </Routes>
                  </SessionGuardWrapper>
                </BrowserRouter>
              </SidebarProvider>
            </MessageProvider>
          </CommonProvider>
        </AgentProvider>
      </ClientProvider>
      </UserProfileProvider>
    </AuthProvider>
  );
}
export default App;
